import React from "react";
import {
  Box,
  Typography,
  TextField,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { User } from "@supabase/supabase-js";
import { TablesInsert } from "../../../database.types";
import { App } from "../../types/types";
import { StandardButton } from "../../components/StandardButton";
import TeamListItem from "./TeamListItem";

interface TeamsTabContentProps {
  teams: App.TeamWithMembers[];
  isOwner: boolean;
  currentUserMembership: App.LeagueMemberWithProfile | null;
  leagueMembers: App.LeagueMemberWithProfile[];
  unassignedMembers: App.LeagueMemberWithProfile[];
  user: User | null;
  newTeam: TablesInsert<"teams">;
  setNewTeam: (team: TablesInsert<"teams">) => void;
  onCreateNewTeam: () => Promise<void>;
  onDeleteTeam: (idx: number) => void;
  onSaveTeam: (idx: number, updatedTeam: App.TeamWithMembers) => void;
  refreshMemberData: () => Promise<void>;
}

export const TeamsTabContent: React.FC<TeamsTabContentProps> = ({
  teams,
  isOwner,
  currentUserMembership,
  leagueMembers,
  unassignedMembers,
  user,
  newTeam,
  setNewTeam,
  onCreateNewTeam,
  onDeleteTeam,
  onSaveTeam,
  refreshMemberData,
}) => {
  const theme = useTheme();

  return (
    <>
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        overflow="hidden"
        sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 2, sm: 3 } }}
      >
        {/* Unassigned Members Section */}
        {unassignedMembers.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.warning.main,
              border: "1px solid",
              borderColor: alpha(theme.palette.warning.main, 0.3),
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              Unassigned Members
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {unassignedMembers.map((member) => (
                <Chip
                  key={member.id}
                  label={
                    member.user_id === user?.id
                      ? `${member.profile.name} (you)`
                      : member.profile.name
                  }
                  variant={
                    member.user_id === user?.id ? "filled" : "outlined"
                  }
                  color={
                    member.user_id === user?.id ? "primary" : "default"
                  }
                  size="small"
                />
              ))}
            </Box>
            {currentUserMembership && !currentUserMembership.team_id && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Join a team below!
              </Typography>
            )}
          </Box>
        )}

        <Box
          display="flex"
          flexDirection="column"
          overflow="hidden"
          gap={2}
        >
          {teams.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No teams added yet.{" "}
              {isOwner
                ? "Add your first team below."
                : "The league owner needs to create teams."}
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                gap: 2,
                overflow: "auto",
              }}
            >
              {teams.map((team, idx) => (
                <TeamListItem
                  key={team.id}
                  team={team}
                  isOwner={isOwner}
                  currentUserMembership={currentUserMembership}
                  allLeagueMembers={leagueMembers}
                  unassignedMembers={unassignedMembers}
                  onDelete={() => onDeleteTeam(idx)}
                  onSaveTeam={(updatedTeam) => onSaveTeam(idx, updatedTeam)}
                  onMemberChange={refreshMemberData}
                />
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {isOwner && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            p: 2,
            flexShrink: 0,
            minWidth: 200,
            width: { xs: "100%", sm: "auto" },
            backgroundColor: theme.palette.primary.dark,
            borderRadius: 2,
          }}
        >
          <TextField
            value={newTeam.name}
            label="Team Name"
            onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
            size="medium"
            fullWidth
            sx={{
              mb: 2,
              "& .MuiInputBase-input": { color: "white" },
              "& .MuiInputLabel-root": { color: "white" },
              "& .MuiInputLabel-root.Mui-focused": { color: "white" },
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "white",
              },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                { borderColor: "white" },
            }}
          />
          <StandardButton
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onCreateNewTeam}
            disabled={!newTeam.name}
            size="medium"
            fullWidth
            sx={{
              color: "white",
              borderColor: "white",
              ":disabled": { color: "gray", borderColor: "gray" },
            }}
          >
            Add Team
          </StandardButton>
        </Box>
      )}
    </>
  );
};
